import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from './ThemeContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faArrowLeft } from '@fortawesome/free-solid-svg-icons';

const FAQPage = () => {
  const subdomain = window.location.hostname.split('.')[0];
  const { theme } = useTheme();
  const { carrierPortal } = theme;
  const navigate = useNavigate();
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`;
    setOpenItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const isAZNational = subdomain === 'localhost' || window.location.hostname.includes('aznational');
  const isAQHA = subdomain === 'aqha';

  const getFAQs = (subdomain, carrierPortal) => {
    return [
      {
        category: "General Questions",
        questions: [
          ...(!carrierPortal ? [{ q: "How do I sign up?", a: <span>You can sign up by completing the <a href="/registration" style={{color: theme.primary}} className="underline hover:opacity-75">registration form.</a></span> }] : []),
          ...(!carrierPortal ? [{ q: "What information is required to sign up?", a: "You will need to provide your name, phone, email, mailing address and provide a payment method for funding your account." }] : []),          
          { q: "Is there an approval process?", a: "There is no approval process; all fuel cards are pre-paid." },
          { q: "Where can I use my card?", a: <span>You can use your card at over 4,000 locations nationwide. Visit our <a href="/fuelinglocations" style={{color: theme.primary}} className="underline hover:opacity-75">locations page</a> for more details.</span> },
          { 
            q: "How much will I save using my card?", 
            a: isAQHA ? "You will save $0.15/gal." : 
               isAZNational ? "You will save up to $0.15/gal on diesel purchases." : 
               "Savings vary based on network discounts." 
          },
          ...(isAQHA ? [{ q: "How will using my card benefit AQHA initiatives?", a: "AQHA Youth Programs will receive an additional $0.05/gal donation." }] : []),
          ...(isAZNational ? [{ q: "How will using my card benefit Arizona National?", a: "Arizona National scholarship programs receive $0.05/gal from every diesel purchase to support student education." }] : []),
        ],
      },
      {
        category: "Card Questions",
        questions: [
          { q: "When will I receive my card?", a: "Your card will arrive in 7 to 10 days by mail." },
          { q: "What should I do if I lose my card?", a: <span>Immediately mark the card as inactive and contact us at <a href="mailto:support@fuellink.net" style={{color: theme.primary}} className="underline hover:opacity-75">support@fuellink.net</a> to order a replacement.</span> },
          { q: "What should I do if my card does not work at the pump?", a: "Check for physical damage and request a replacement if needed. Ensure that sufficient funds are available for fuel purchase." },
          { q: "How can I check my available balance?", a: "You can check your balance on the fuel portal." },
          { q: "How can I increase my available balance?", a: "You can increase your balance via ACH transfer on the fuel portal." },
          { q: "Can I view my card history?", a: "Yes, you can view your card history in the reports tab on the fuel portal." },
          { q: "How many gallons can I purchase and how frequently?", a: "You can purchase up to the designated funds available on your card." },
          { q: "I need more assistance, who can I contact for help?", a: <span>You can contact support by email at <a href="mailto:support@fuellink.net" style={{color: theme.primary}} className="underline hover:opacity-75">support@fuellink.net</a> or by calling (801) 828-6936.</span> },
        ],
      },
      {
        category: "Banking Questions",
        questions: [
          { q: "What banking information is required?", a: "You need to provide your bank account and routing number." },
          { q: "Is my banking data secure?", a: "Yes, your banking data is securely processed via encrypted ACH transfer." },
          { q: "How do you initially transfer money from my bank to my card?", a: "We use ACH to directly transfer money from your bank account to your card." },
          { q: "How are funds replenished after I purchase fuel?", a: <span>If auto-reload is on, when the balance is 25% less than of the desired amount, we will automatically draft the necessary funds to restore the balance. This feature can be disabled in the <a href="/settings" style={{color: theme.primary}} className="underline hover:opacity-75">settings tab</a>.</span> },
          { q: "What does a successful ACH notification mean?", a: "A successful ACH notification means that your funds have been successfully posted to your fuel card account." },
          { q: "What does a failed ACH notification mean?", a: "A failed ACH notification indicates that there were insufficient funds in your bank account, and a $6.00 fee has been charged to your account." },
        ],
      },
    ];
  };

  const faqs = getFAQs(subdomain, carrierPortal);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center mb-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-gray-600 hover:text-gray-800 transition-colors mr-4"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
              Back
            </button>
          </div>
          <h1 className="text-4xl font-bold" style={{color: theme.primaryText || theme.primary}}>
            Frequently Asked Questions
          </h1>
          <p className="text-gray-600 mt-2">
            Find answers to common questions about our fuel program
          </p>
        </div>
      </div>

      {/* FAQ Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {faqs.map((section, categoryIndex) => (
          <div key={categoryIndex} className="mb-8">
            <h2 
              className="text-2xl font-semibold mb-6 pb-2 border-b-2" 
              style={{
                color: theme.primaryText || theme.primary,
                borderBottomColor: theme.secondary || theme.primary
              }}
            >
              {section.category}
            </h2>
            <div className="space-y-3">
              {section.questions.map((faq, questionIndex) => {
                const isOpen = openItems[`${categoryIndex}-${questionIndex}`];
                return (
                  <div 
                    key={questionIndex} 
                    className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                  >
                    <button
                      onClick={() => toggleItem(categoryIndex, questionIndex)}
                      className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <h3 className="text-lg font-medium text-gray-900 pr-4">
                        {faq.q}
                      </h3>
                      <FontAwesomeIcon 
                        icon={isOpen ? faChevronUp : faChevronDown} 
                        className="text-gray-400 flex-shrink-0"
                        style={{color: theme.primary}}
                      />
                    </button>
                    {isOpen && (
                      <div className="px-6 pb-4">
                        <div className="pt-2 border-t border-gray-100">
                          <p className="text-gray-700 leading-relaxed">
                            {faq.a}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        
        {/* Bottom CTA */}
        <div className="mt-12 text-center bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <h3 className="text-xl font-semibold mb-4" style={{color: theme.primaryText || theme.primary}}>
            Still have questions?
          </h3>
          <p className="text-gray-600 mb-6">
            Our support team is ready to help you get the most out of your fuel program.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="mailto:support@fuellink.net"
              className="inline-flex items-center justify-center px-6 py-3 rounded-lg font-medium transition-colors"
              style={{
                backgroundColor: theme.primary,
                color: 'white'
              }}
              onMouseOver={(e) => e.target.style.opacity = '0.9'}
              onMouseOut={(e) => e.target.style.opacity = '1'}
            >
              Email Support
            </a>
            <a 
              href="tel:+18018286936"
              className="inline-flex items-center justify-center px-6 py-3 border-2 rounded-lg font-medium transition-colors"
              style={{
                borderColor: theme.primary,
                color: theme.primary
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = theme.primary;
                e.target.style.color = 'white';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = theme.primary;
              }}
            >
              Call (801) 828-6936
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;



// import React from 'react';
// import { useNavigate } from 'react-router-dom';
// import { useTheme } from './ThemeContext';

// const getFAQs = (subdomain) => {
  
//   const faqs = [
//     {
//       category: "General Questions",
//       questions: [
//         ...(!carrierPortal ? [] : [{ q: "How do I sign up?", a: <span>You can sign up by completing the <a href="/registration" className="text-blue-500 underline">registration form.</a></span> }]),
//         { q: "What information is required to sign up?", a: "You will need to provide your name, phone, email, mailing address and provide a payment method for funding your account." },
//         { q: "Is my data secure?", a: "Yes, your data is securely processed via encrypted ACH transfer." },
//         { q: "Is there an approval process?", a: "There is no approval process; all fuel cards are pre-paid." },
//         { q: "Where can I use my card?", a: <span>You can use your card at over 4,000 locations nationwide. Visit our <a href="/fuelinglocations" className="text-blue-500 underline">locations page</a> for more details.</span> },
//         { q: "How much will I save using my card?", a: subdomain === 'aqha' ? "You will save $0.15 per gallon." : "Savings vary based on network discounts." },
//         ...(subdomain === 'aqha' ? [{ q: "How will using my card benefit initiatives?", a: "Programs will receive an additional $0.05 per gallon donation." }] : []),
//       ],
//     },
//     {
//       category: "Card Questions",
//       questions: [
//         { q: "When will I receive my card?", a: "Your card will arrive in 7 to 10 days by mail." },
//         { q: "What should I do if I lose my card?", a: <span>Immediately mark the card as inactive and contact us at <a href="mailto:support@fuellink.net" className="text-blue-500 underline">support@fuellink.net</a> to order a replacement.</span> },
//         { q: "What should I do if my card does not work at the pump?", a: "Check for physical damage and request a replacement if needed. Ensure that sufficient funds are available for fuel purchase." },
//         { q: "How can I check my available balance?", a: "You can check your balance on the fuel portal." },
//         { q: "How can I increase my available balance?", a: "You can increase your balance via ACH transfer on the fuel portal." },
//         { q: "Can I view my card history?", a: "Yes, you can view your card history in the reports tab on the fuel portal." },
//         { q: "How many gallons can I purchase and how frequently?", a: "You can purchase up to the designated funds available on your card." },
//         { q: "I need more assistance, who can I contact for help?", a: <span>You can contact support by email at <a href="mailto:support@fuellink.net" className="text-blue-500 underline">support@fuellink.net</a> or by calling (801) 828-6936.</span> },
//       ],
//     },
//     {
//       category: "Banking Questions",
//       questions: [
//         { q: "What banking information is required?", a: "You need to provide your bank account and routing number." },
//         { q: "Is my banking data secure?", a: "Yes, your banking data is securely processed via encrypted ACH transfer." },
//         { q: "How do you initially transfer money from my bank to my card?", a: "We use ACH to directly transfer money from your bank account to your card." },
//         { q: "How are funds replenished after I purchase fuel?", a: <span>By default, your card is refilled to the desired balance set during account setup. When the balance reaches 40% of the desired amount, we will automatically draft the necessary funds to restore the balance. This feature can be disabled in the <a href="/settings" className="text-blue-500 underline">settings tab</a>.</span> },
//         { q: "What does a successful ACH notification mean?", a: "A successful ACH notification means that your funds have been successfully posted to your fuel card account." },
//         { q: "What does a failed ACH notification mean?", a: "A failed ACH notification indicates that there were insufficient funds in your bank account, and a $6.00 fee has been charged to your account." },
//       ],
//     },
//   ];
//   return faqs;
// };

// const FAQPage = () => {
//   const subdomain = window.location.hostname.split('.')[0];
//   const faqs = getFAQs(subdomain);
//   const navigate = useNavigate();
//   const { theme } = useTheme();
//   const { carrierPortal } = theme;  

//   return (
//     <div className="max-w-7xl mx-auto p-6 sm:p-10">
      
//       <h1 className="text-4xl font-bold mb-8">Frequently Asked Questions</h1>
//       <button
//         onClick={() => navigate(-1)}
//         className="themed-button py-2 px-4 mb-4"
//       >
//         Back
//       </button>
//       {faqs.map((section, index) => (
//         <div key={index} className="mb-10">
//           <h2 className="text-2xl font-semibold mb-4">{section.category}</h2>
//           <div className="space-y-4">
//             {section.questions.map((faq, i) => (
//               <div key={i} className="bg-gray-100 p-4 rounded-md shadow-md">
//                 <h3 className="text-xl font-medium">{faq.q}</h3>
//                 <p className="mt-2 text-gray-700">{faq.a}</p>
//               </div>
//             ))}
//           </div>
//         </div>
//       ))}
//       <button
//         onClick={() => navigate(-1)}
//         className="themed-button py-2 px-4"
//       >
//         Back
//       </button>
//     </div>
//   );
// };

// export default FAQPage;
